import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Building2, Users, DollarSign, Activity, ArrowRightLeft, Trophy, Medal, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from "recharts";

const RANK_ICON = { 0: Trophy, 1: Medal, 2: Award };
const RANK_COLOR = { 0: "text-amber-500", 1: "text-slate-400", 2: "text-orange-400" };

export default function UnitsDashboard() {
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState("monthly_revenue");

  useEffect(() => {
    api.get("/units/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-slate-500">Carregando...</div>;

  const ranked = [...data.units].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));

  // Normalize each metric to 0-100 for radar comparison
  const maxRev = Math.max(1, ...data.units.map(u => u.monthly_revenue));
  const maxStd = Math.max(1, ...data.units.map(u => u.active_students));
  const maxAtt = Math.max(1, ...data.units.map(u => u.attendance_30d));

  const radarData = [
    { metric: "Receita", ...Object.fromEntries(data.units.map(u => [u.unit_name, Math.round((u.monthly_revenue / maxRev) * 100)])) },
    { metric: "Alunos", ...Object.fromEntries(data.units.map(u => [u.unit_name, Math.round((u.active_students / maxStd) * 100)])) },
    { metric: "Frequência", ...Object.fromEntries(data.units.map(u => [u.unit_name, Math.round((u.attendance_30d / maxAtt) * 100)])) },
  ];

  const RADAR_COLORS = ["#E11D48", "#09090B", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6"];

  const totals = data.totals;

  const sortLabels = {
    monthly_revenue: "Receita mensal",
    active_students: "Alunos ativos",
    attendance_30d: "Frequência 30d",
    cross_visits_30d: "Visitas cruzadas",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Painel Consolidado</h1>
          <p className="text-slate-500">Comparativo entre unidades da rede — apenas administradores</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-500">Ordenar por</span>
          <select value={sortBy} onChange={(e)=>setSortBy(e.target.value)}
            data-testid="units-dashboard-sort"
            className="text-sm px-3 py-1.5 border border-slate-300 rounded-lg bg-white">
            {Object.entries(sortLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-4 border border-slate-200">
          <Building2 className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Unidades</div>
          <div className="font-heading text-2xl font-bold text-slate-900">{totals.units}</div>
        </Card>
        <Card className="p-4 border border-slate-200">
          <Users className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Alunos totais</div>
          <div className="font-heading text-2xl font-bold text-slate-900">{totals.total_active_students}</div>
        </Card>
        <Card className="p-4 border border-slate-200">
          <DollarSign className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Receita/mês</div>
          <div className="font-heading text-2xl font-bold text-slate-900">R$ {totals.total_monthly_revenue.toFixed(2)}</div>
        </Card>
        <Card className="p-4 border border-slate-200">
          <Activity className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Acessos 30d</div>
          <div className="font-heading text-2xl font-bold text-slate-900">{totals.total_attendance_30d}</div>
        </Card>
        <Card className="p-4 border border-slate-200">
          <ArrowRightLeft className="w-5 h-5 text-rose-600 mb-2"/>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Cross-unit 30d</div>
          <div className="font-heading text-2xl font-bold text-slate-900">{totals.total_cross_visits_30d}</div>
        </Card>
      </div>

      {/* Ranking */}
      <Card className="p-5 border border-slate-200">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">🏆 Ranking por {sortLabels[sortBy]}</h3>
        <div className="space-y-2" data-testid="units-ranking-list">
          {ranked.map((u, idx) => {
            const Icon = RANK_ICON[idx];
            const color = RANK_COLOR[idx] || "text-slate-300";
            return (
              <div key={u.unit_id} className={`grid grid-cols-12 gap-3 items-center p-3 rounded-xl border ${idx < 3 ? "bg-gradient-to-r from-slate-50 to-white border-slate-200" : "border-slate-100"}`}>
                <div className="col-span-1 text-center">
                  {Icon ? <Icon className={`w-6 h-6 ${color} mx-auto`}/> : <span className="font-heading font-bold text-slate-400">#{idx + 1}</span>}
                </div>
                <div className="col-span-3">
                  <div className="font-heading font-bold text-slate-900">{u.unit_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.address || "—"}</div>
                </div>
                <div className="col-span-2 text-center">
                  <div className="font-heading text-lg font-bold text-slate-900">R$ {u.monthly_revenue.toFixed(2)}</div>
                  <div className="text-xs text-slate-500">receita/mês</div>
                </div>
                <div className="col-span-2 text-center">
                  <div className="font-heading text-lg font-bold text-slate-900">{u.active_students}</div>
                  <div className="text-xs text-slate-500">alunos</div>
                </div>
                <div className="col-span-2 text-center">
                  <div className="font-heading text-lg font-bold text-slate-900">{u.attendance_30d}</div>
                  <div className="text-xs text-slate-500">acessos 30d</div>
                </div>
                <div className="col-span-2 text-center">
                  <div className={`font-heading text-lg font-bold ${u.cross_visits_30d > 0 ? "text-rose-600" : "text-slate-400"}`}>{u.cross_visits_30d}</div>
                  <div className="text-xs text-slate-500">cross-unit</div>
                </div>
              </div>
            );
          })}
          {ranked.length === 0 && <p className="text-center text-slate-500 py-8">Nenhuma unidade cadastrada</p>}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-slate-200">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Receita mensal por unidade</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ranked} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }}/>
                <YAxis type="category" dataKey="unit_name" tick={{ fontSize: 11 }} width={100}/>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v)=>`R$ ${v.toFixed(2)}`}/>
                <Bar dataKey="monthly_revenue" fill="#E11D48" radius={[0, 6, 6, 0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border border-slate-200">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Comparativo normalizado (0-100)</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0"/>
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }}/>
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }}/>
                {data.units.map((u, i) => (
                  <Radar key={u.unit_id} name={u.unit_name} dataKey={u.unit_name}
                    stroke={RADAR_COLORS[i % RADAR_COLORS.length]}
                    fill={RADAR_COLORS[i % RADAR_COLORS.length]}
                    fillOpacity={0.15}/>
                ))}
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }}/>
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
